import React from "react";
import { Button, Modal } from "react-bootstrap";

import classNames from "classnames";

import { defaultDateTimeFormat } from "../lib/utils";
import { IGame, IUser } from "../types/api";

interface ModalProps {
    show: boolean;
    action: string;
    game: IGame;
    handleClose: (key: string) => void;
    handleAction: (action: string, gameId: IGame["id"]) => void;
}

const gameBody = (game: IGame, action: string) => {
    return (
        <>
            <h5>Are you sure you want to {action} this game?</h5>
            <ul style={{ listStyleType: "none" }}>
                <li>
                    <b>Game Date: </b>{" "}
                    {defaultDateTimeFormat.format(new Date(game.date))}
                </li>
                <li>
                    <b>Home: </b>
                    {game.home.name}
                </li>
                <li>
                    <b>Visitor: </b>
                    {game.visitor.name}
                </li>
                <li>
                    <b>Location: </b>
                    {game.fieldName}
                </li>
                {game.diamond && (
                    <>
                        <b>Diamond: </b>
                        {game.diamond}
                    </>
                )}
            </ul>
            {action === "reset" ? (
                <strong className={classNames("text-danger")}>
                    Resetting this game will delete all submitted data and set
                    the game back to a "Scheduled" status
                    {game.isPlayoff ? (
                        <>
                            <br />
                            Additionally, this will undo the advancement of the
                            winning team to the next round of the playoffs thus
                            removing them from that game.
                        </>
                    ) : (
                        ""
                    )}
                </strong>
            ) : (
                ""
            )}
        </>
    );
};

const rescheduleBody = (game: IGame, action: string) => {
    return (
        game.rescheduleInfo && (
            <>
                <h5>
                    Are you sure you want to {action} this reschedule request?
                </h5>
                <h6>Original Game Details</h6>
                <ul style={{ listStyleType: "none" }}>
                    <li>
                        <b>Game Date: </b>{" "}
                        {defaultDateTimeFormat.format(new Date(game.date))}
                    </li>
                    <li>
                        <b>Location: </b>
                        {game.fieldName}
                    </li>
                    {game.diamond && (
                        <>
                            <b>Diamond: </b>
                            {game.diamond}
                        </>
                    )}
                </ul>

                <h6>Requested Rescheduled Game Details</h6>
                <ul style={{ listStyleType: "none" }}>
                    <li>
                        <b>Game Date: </b>{" "}
                        {defaultDateTimeFormat.format(
                            new Date(game.rescheduleInfo.date),
                        )}
                    </li>
                    <li>
                        <b>Location: </b>
                        {game.rescheduleInfo.fieldName}
                    </li>
                    {game.rescheduleInfo.diamond && (
                        <>
                            <b>Diamond: </b>
                            {game.rescheduleInfo.diamond}
                        </>
                    )}
                </ul>
            </>
        )
    );
};

const GameActionModal = ({
    show,
    action,
    game,
    handleClose,
    handleAction,
}: ModalProps) => {
    const buildModalBody = () => {
        switch (action) {
            case "delete":
            case "postpone":
            case "reset":
                return gameBody(game, action);
            case "cancel":
            case "decline":
            case "accept":
                return rescheduleBody(game, action);
        }
    };

    return (
        <Modal
            show={show}
            onHide={() => handleClose(action)}
            data-testid="gameActionModal"
        >
            <Modal.Header>
                <Modal.Title>{`${
                    action.charAt(0).toUpperCase() + action.slice(1)
                } Game`}</Modal.Title>
            </Modal.Header>
            <Modal.Body>{buildModalBody()}</Modal.Body>
            <Modal.Footer>
                <Button variant="primary" onClick={() => handleClose(action)}>
                    No
                </Button>
                <Button
                    variant="danger"
                    onClick={() => {
                        handleAction(action, game.id);
                    }}
                >
                    Yes
                </Button>
            </Modal.Footer>
        </Modal>
    );
};

export default GameActionModal;
